// screens/marketplace/CoursePricing.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  SlidersHorizontal,
  Plus,
  Trash2,
  Pencil,
  X,
  GraduationCap,
  Users,
  ShoppingBag,
  AlertCircle,
  Save,
  RefreshCw,
  Search,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Field,
  Input,
  EmptyState,
  LoadingBlock,
  useTokens,
} from "./shared";
import {
  COURSE_PRICE_TYPES,
  availableCoursePriceTypes,
  coursePriceTypeMeta,
  listCoursePricing,
  saveCoursePricing,
  deleteCoursePricing,
} from "./coursesManageApi";
import { listCourses } from "./coursesApi";

const CONTEXTS = [
  {
    id: "customer",
    title: "My customers",
    description: "Prices buyers see on my storefront.",
    icon: ShoppingBag,
  },
  {
    id: "subAgent",
    title: "My sub-agents",
    description: "Costs my sub-agents pay.",
    icon: Users,
  },
];

export default function CoursePricing() {
  const { authFetch, user } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const role = user?.role || "AGENT";
  const canSeeSubAgentContext = role === "ADMIN" || role === "AGENT";

  const [context, setContext] = useState("customer");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [courses, setCourses] = useState([]);
  const [subAgents, setSubAgents] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const fetches = [
          listCourses(authFetch),
          listCoursePricing(authFetch),
        ];
        if (canSeeSubAgentContext) {
          fetches.push(
            authFetch("/accounts/agent/sub-agents/").then((r) => r.json())
          );
        }
        const [coursesData, pricingData, subData] = await Promise.all(fetches);
        setCourses(coursesData || []);
        setOverrides(pricingData || []);
        if (canSeeSubAgentContext && subData?.data?.sub_agents) {
          setSubAgents(subData.data.sub_agents);
        }
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, canSeeSubAgentContext, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const overridesByCourse = useMemo(() => {
    const map = new Map();
    overrides.forEach((o) => {
      if (!map.has(o.course)) map.set(o.course, []);
      map.get(o.course).push(o);
    });
    return map;
  }, [overrides]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? courses.filter((c) => c.title.toLowerCase().includes(q))
      : courses;
    return list.map((c) => ({
      course: c,
      overrides: overridesByCourse.get(c.id) || [],
    }));
  }, [courses, overridesByCourse, search]);

  const handleSave = async (payload) => {
    const saved = await saveCoursePricing(authFetch, payload);
    setOverrides((prev) => {
      const key = (o) => `${o.course}:${o.user}:${o.price_type}`;
      const savedKey = key(saved);
      return [...prev.filter((o) => key(o) !== savedKey), saved];
    });
    showToast("Price saved", "success");
    setModal(null);
  };

  const handleDelete = async (row) => {
    const meta = coursePriceTypeMeta(row.price_type);
    if (
      !window.confirm(
        `Remove the "${meta.label}" override for this course? Fallback price applies.`
      )
    )
      return;
    setBusyId(row.id);
    try {
      await deleteCoursePricing(authFetch, row.id);
      setOverrides((prev) => prev.filter((o) => o.id !== row.id));
      showToast("Override removed", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell
      eyebrow="Courses"
      title="Course pricing"
      subtitle="Set what your customers pay and what your sub-agents pay. Every price falls back to the course base if no override exists."
      actions={
        <>
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => load({ silent: true })}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            icon={Plus}
            onClick={() => setModal({ mode: "create", context })}
          >
            Add price
          </Button>
        </>
      }
    >
      {canSeeSubAgentContext && (
        <div style={styles.contextTabs}>
          {CONTEXTS.map((c) => {
            const Icon = c.icon;
            const active = context === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setContext(c.id)}
                style={styles.contextTab(t, active)}
              >
                <span style={styles.contextIcon(t, active)}>
                  <Icon size={15} />
                </span>
                <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: active ? t.heading : t.text,
                    }}
                  >
                    {c.title}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 11.5,
                      color: t.muted,
                      marginTop: 2,
                    }}
                  >
                    {c.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ position: "relative", maxWidth: 380, marginBottom: 14 }}>
        <Search
          size={14}
          color={t.muted}
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
        <Input
          placeholder="Search courses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ paddingLeft: 36 }}
        />
      </div>

      {loading ? (
        <LoadingBlock label="Loading pricing…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          title="No courses available"
          description="Create courses first, then set their pricing here."
        />
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map(({ course, overrides }) => (
            <CourseRow
              key={course.id}
              course={course}
              overrides={overrides}
              context={context}
              role={role}
              subAgents={subAgents}
              t={t}
              onAdd={(priceType) =>
                setModal({
                  mode: "create",
                  context,
                  courseId: course.id,
                  presetPriceType: priceType,
                })
              }
              onEdit={(row) => setModal({ mode: "edit", row, context })}
              onDelete={handleDelete}
              busyId={busyId}
            />
          ))}
        </div>
      )}

      {modal && (
        <PriceEditorModal
          modal={modal}
          role={role}
          user={user}
          courses={courses}
          subAgents={subAgents}
          t={t}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Row
   ══════════════════════════════════════════════════════════ */

const CourseRow = ({
  course,
  overrides,
  context,
  role,
  user,
  subAgents,
  t,
  onAdd,
  onEdit,
  onDelete,
  busyId,
}) => {
  const contextOverrides = overrides.filter((o) => {
    const meta = coursePriceTypeMeta(o.price_type);
    if (context === "customer") return meta.scope === "customer-facing";
    return (
      meta.scope === "sub-agent-cost" ||
      meta.scope === "sub-agent-cost-override"
    );
  });

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        background: t.surfaceRaised,
        border: `1px solid ${t.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              overflow: "hidden",
              background: t.subtle,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: t.muted,
            }}
          >
            {course.cover_image_url ? (
              <img
                src={course.cover_image_url}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <GraduationCap size={20} />
            )}
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: t.heading,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {course.title}
            </div>
            <div style={{ fontSize: 11.5, color: t.muted, marginTop: 2 }}>
              Base ₵{Number(course.price).toFixed(2)} · agent ₵
              {Number(course.base_price_agent).toFixed(2)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onAdd(null)}
          title="Add override"
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            border: `1px dashed ${t.border}`,
            background: "transparent",
            color: t.muted,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Plus size={14} />
        </button>
      </div>

      {contextOverrides.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {contextOverrides.map((o) => {
            const meta = coursePriceTypeMeta(o.price_type);
            const subName =
              meta.scope === "sub-agent-cost-override"
                ? subAgents.find((s) => s.user_id === o.user)?.username ||
                  o.username
                : null;
            const busy = busyId === o.id;
            return (
              <span
                key={o.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: t.successBg,
                  color: t.success,
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    maxWidth: 240,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {meta.label}
                  {subName ? ` · @${subName}` : ""}
                </span>
                <span
                  style={{
                    fontWeight: 800,
                    color: t.heading,
                    paddingLeft: 6,
                    borderLeft: `1px solid ${t.separator || t.border}`,
                  }}
                >
                  ₵{Number(o.price).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => onEdit(o)}
                  disabled={busy}
                  style={chipBtn(t)}
                  title="Edit"
                >
                  <Pencil size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(o)}
                  disabled={busy}
                  style={chipBtn(t, true)}
                  title="Remove"
                >
                  {busy ? (
                    <RefreshCw
                      size={11}
                      style={{ animation: "spin 0.9s linear infinite" }}
                    />
                  ) : (
                    <Trash2 size={11} />
                  )}
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

const chipBtn = (t, danger) => ({
  width: 22,
  height: 22,
  borderRadius: 6,
  border: "none",
  background: "transparent",
  color: danger ? t.error : "inherit",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

/* ══════════════════════════════════════════════════════════
   Price modal
   ══════════════════════════════════════════════════════════ */

const PriceEditorModal = ({
  modal,
  role,
  user,
  courses,
  subAgents,
  t,
  onClose,
  onSave,
}) => {
  const isEdit = modal.mode === "edit";
  const availableTypes = useMemo(
    () => availableCoursePriceTypes(role, modal.context),
    [role, modal.context]
  );

  const [course, setCourse] = useState(
    modal.row?.course || modal.courseId || ""
  );
  const [priceType, setPriceType] = useState(
    modal.row?.price_type || modal.presetPriceType || availableTypes[0]?.value || ""
  );
  const [targetUser, setTargetUser] = useState(modal.row?.user || "");
  const [price, setPrice] = useState(modal.row?.price || "");
  const [notes, setNotes] = useState(modal.row?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const needsSubAgentPicker =
    !isEdit &&
    priceType === COURSE_PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE.value &&
    (role === "AGENT" || role === "ADMIN");

  const selectedCourse = courses.find((c) => c.id === course);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!course) {
      setErrors({ course: "Choose a course" });
      return;
    }
    if (!price || Number(price) <= 0) {
      setErrors({ price: "Enter a valid price" });
      return;
    }
    if (!isEdit && needsSubAgentPicker && !targetUser) {
      setErrors({ targetUser: "Select a sub-agent" });
      return;
    }

    const payload = { course, price_type: priceType, price, notes };

    if (!isEdit) {
      // The backend expects `user` on every pricing row. Two cases:
      //   • Pricing a specific sub-agent → user = sub-agent's user_id
      //   • Self-targeted pricing → user = caller's own user id
      payload.user = needsSubAgentPicker ? targetUser : user.id;
    }

    setSubmitting(true);
    try {
      await onSave(payload);
    } catch (err) {
      setErrors({ _general: err.message });
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: t.scrim,
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: t.surfaceRaised,
          border: `1px solid ${t.border}`,
          borderRadius: 16,
          width: "100%",
          maxWidth: 480,
          padding: 24,
          boxShadow: `0 24px 64px ${t.shadowStrong}`,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: t.heading,
                letterSpacing: "-0.01em",
              }}
            >
              {isEdit ? "Edit price override" : "Add price override"}
            </div>
            <div style={{ fontSize: 12.5, color: t.muted, marginTop: 3 }}>
              {modal.context === "customer"
                ? "What your customers pay"
                : "What your sub-agents pay"}
            </div>
          </div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} />
        </header>

        {!isEdit && (
          <Field label="Course" error={errors.course}>
            <select
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              style={selectStyle(t)}
            >
              <option value="">Choose course</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </Field>
        )}

        {isEdit && modal.row?.course_title && (
          <div style={previewBox(t)}>
            <div style={previewLabel(t)}>Course</div>
            <div style={previewValue(t)}>{modal.row.course_title}</div>
          </div>
        )}

        <Field label="Price type">
          <select
            value={priceType}
            onChange={(e) => setPriceType(e.target.value)}
            disabled={isEdit}
            style={selectStyle(t)}
          >
            {availableTypes.map((ty) => (
              <option key={ty.value} value={ty.value}>
                {ty.label}
              </option>
            ))}
          </select>
        </Field>

        {needsSubAgentPicker && (
          <Field label="Target sub-agent" error={errors.targetUser}>
            <select
              value={targetUser}
              onChange={(e) => setTargetUser(e.target.value)}
              style={selectStyle(t)}
            >
              <option value="">Choose sub-agent</option>
              {subAgents.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {s.username || s.full_name || s.user_id}
                </option>
              ))}
            </select>
          </Field>
        )}

        {selectedCourse && (
          <div style={previewBox(t)}>
            <div style={previewLabel(t)}>Fallback price</div>
            <div style={previewValue(t)}>
              ₵{Number(selectedCourse.base_price_agent).toFixed(2)}
            </div>
            <div style={{ fontSize: 11.5, color: t.muted }}>
              Your price must be at least this much.
            </div>
          </div>
        )}

        <Field label="Your price" error={errors.price}>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
          />
        </Field>

        <Field label="Notes (optional)">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why this price?"
          />
        </Field>

        {errors._general && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 10,
              background: t.errorBg,
              color: t.error,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            <AlertCircle size={14} />
            {errors._general}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 4,
          }}
        >
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" icon={Save} disabled={submitting}>
            {submitting ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const selectStyle = (t) => ({
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${t.border}`,
  background: t.raisedBg,
  color: t.text,
  fontSize: 14,
  outline: "none",
  fontFamily: "inherit",
});

const previewBox = (t) => ({
  background: t.mainBg,
  borderRadius: 10,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});
const previewLabel = (t) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: t.muted,
});
const previewValue = (t) => ({
  fontSize: 14,
  fontWeight: 800,
  color: t.heading,
});

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  contextTabs: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  contextTab: (t, active) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: 14,
    border: `1px solid ${active ? t.accent : t.border}`,
    background: active ? t.hoverBg : t.raisedBg,
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
    fontFamily: "inherit",
  }),
  contextIcon: (t, active) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: active ? t.accent : t.hoverBg,
    color: active ? t.accentText : t.muted,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
};

if (typeof window !== "undefined" && !document.getElementById("cp-spin")) {
  const el = document.createElement("style");
  el.id = "cp-spin";
  el.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(el);
}